import React from "react";
import type { ApolloClient } from "@apollo/client";
import classnames from "classnames";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { auth0AccountSessionSelector } from "store/selectors/auth0";
import { NavDropdown } from "react-bootstrap";
import { get, map, size, isEmpty } from "lodash";
import { accountConstants } from "actions/types";
import { createLoadingSelector } from "store/selectors";
import { setAccountSession } from "actions/accountActions";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import {
  isIndividualAccountOverview,
  activeAccountType,
} from "store/selectors/user";
import { FiChevronDown, FiCheck } from "react-icons/fi";
import {
  IndividualInvestmentProfileUpdate,
  IndividualPortfolioUpdate,
  IndividualRequiredUpdatesPending,
} from "statics/states";
import { investmentAccountsSelector } from "store/selectors/accounts";
import type { RootState } from "types/state";

import "./AccountSwitcher.scss";

/** Shape of each required-update entry (mirrors the former propTypes). */
interface PendingRequiredUpdate {
  entityIdentifier: {
    id: string;
    type: string;
  };
  pendingSteps?: string[];
  accountType?: string;
}

// Parent passes nothing — all props come from redux (and `client` from
// withApollo), so there are no OwnProps and Props is exactly PropsFromRedux.

const loadingSelector = createLoadingSelector(
  accountConstants.SET_ACCOUNT_SESSION
);

const mapStateToProps = (state: RootState) => {
  const accounts = investmentAccountsSelector(state);
  const userState = get(state, "user.userState.state") as string | undefined;
  return {
    isFetching: loadingSelector(state),
    activeAccountId: auth0AccountSessionSelector(state),
    isAccountOverview: isIndividualAccountOverview(state),
    activeAccountType: activeAccountType(state),
    accounts,
    userState,
    pendingRequiredUpdates: get(
      state,
      "user.requiredUpdates.updates",
      []
    ) as PendingRequiredUpdate[],
  };
};

const mapDispatchToProps = {
  setAccountSession,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type AccountSwitcherProps = WithApolloClient<PropsFromRedux>;

interface AccountSwitcherState {
  rotateChevron: boolean;
  hasSetInitialAccount: boolean;
}

class AccountSwitcher extends React.PureComponent<
  AccountSwitcherProps,
  AccountSwitcherState
> {
  constructor(props: AccountSwitcherProps) {
    super(props);

    this.state = {
      rotateChevron: false,
      hasSetInitialAccount: false,
    };
  }

  componentDidMount() {
    void this._setInitialAccountIfNeeded();
  }

  componentDidUpdate(prevProps: AccountSwitcherProps) {
    if (
      size(prevProps.accounts) === 0 &&
      size(this.props.accounts) > 0 &&
      !this.state.hasSetInitialAccount
    ) {
      void this._setInitialAccountIfNeeded();
    }
  }

  _setInitialAccountIfNeeded = async () => {
    const { accounts, client, setAccountSession, activeAccountId } = this.props;
    const { hasSetInitialAccount } = this.state;

    if (
      hasSetInitialAccount ||
      size(accounts) === 0 ||
      !isEmpty(activeAccountId)
    ) {
      return;
    }

    const isSingleAccount = size(accounts) === 1;
    if (isSingleAccount) {
      // get() returns string | undefined for an optional id; the runtime
      // contract here is always a present id, so cast to string.
      const accountId = get(accounts, "[0].id") as string;
      await setAccountSession(client as ApolloClient<object>, accountId);
      this.setState({ hasSetInitialAccount: true });
    } else {
      this.setState({ hasSetInitialAccount: true });
    }
  };

  _handleRotate = () => {
    this.setState({ rotateChevron: !this.state.rotateChevron });
  };

  _activeAccountName = () => {
    const activeAccount = this.props.isAccountOverview
      ? "OVERVIEW"
      : this.props.activeAccountType;

    const showDropdown = !this.disableAccountSwitcher();
    return (
      <>
        <div className="account-switcher-target-box">
          <p className="active-account-name">
            {
              accountTypesToEnglishMapping[
                // activeAccount may be null or a non-key string at runtime
                // (then the lookup is undefined) — cast to satisfy the index.
                activeAccount as keyof typeof accountTypesToEnglishMapping
              ]
            }
            {showDropdown && (
              <span className="down-arrow">
                <FiChevronDown
                  size={14}
                  className={classnames(
                    `nav-chevron  ${this.state.rotateChevron ? "rotate" : ""}`
                  )}
                />
              </span>
            )}
          </p>
        </div>
      </>
    );
  };

  _displayOverview = () => {
    return (
      <>
        <NavDropdown.Item
          className={classnames("action-item ", {
            ["is-selected"]: this.props.isAccountOverview,
          })}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises -- pre-existing fire-and-forget async handler, not changed in conversion
          onClick={async () => {
            await this.props.setAccountSession(
              this.props.client as ApolloClient<object>,
              ""
            );
            window.location.search = `?redirect_to=/dashboard`;
          }}
        >
          Overview
          {this.props.isAccountOverview && (
            <FiCheck stroke={"#0A2540"} size={14} />
          )}
        </NavDropdown.Item>
      </>
    );
  };

  filteredAccounts = () => {
    const { pendingRequiredUpdates = [] } = this.props;
    const isRequiredUpdatesPending =
      this.props.userState === IndividualRequiredUpdatesPending;
    if (!isRequiredUpdatesPending) {
      return this.props.accounts;
    }

    const pendingAccountIds = pendingRequiredUpdates
      .filter((update) => update.entityIdentifier.type === "BankAccount")
      .map((update) => update.entityIdentifier.id);

    return this.props.accounts.filter((account) =>
      // account.id is Maybe<string>; includes() needs string (a null/undefined
      // id simply never matches at runtime).
      pendingAccountIds.includes(account.id as string)
    );
  };

  _getAccountTypes = () => {
    const accounts = this.filteredAccounts();
    return (
      <div className="action-list-popover account-switcher-dropdown">
        <div className="actions-list">
          {this.displayOverviewOption() && this._displayOverview()}
          {map(accounts, (account) => {
            return (
              <NavDropdown.Item
                key={account.id}
                className={classnames("action-item", {
                  ["is-selected"]: this.props.activeAccountId === account.id,
                })}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises -- pre-existing fire-and-forget async handler, not changed in conversion
                onClick={async () => {
                  await this.props.setAccountSession(
                    this.props.client as ApolloClient<object>,
                    // account.id is Maybe<string>; the dispatch expects string.
                    account.id as string
                  );
                  window.location.search = `?redirect_to=/dashboard`;
                }}
              >
                {
                  accountTypesToEnglishMapping[
                    // accountType is Maybe<string>; unknown/missing keys
                    // resolve to undefined at runtime — cast for the index.
                    account.accountType as keyof typeof accountTypesToEnglishMapping
                  ]
                }{" "}
                {this.props.activeAccountId === account.id && (
                  <FiCheck stroke={"#0A2540"} size={14} />
                )}
              </NavDropdown.Item>
            );
          })}
        </div>
      </div>
    );
  };

  disableAccountSwitcher = () => {
    const isSingleAccount = size(this.props.accounts) === 1;
    const isRequiredUpdatesPending =
      this.props.userState === IndividualRequiredUpdatesPending;
    const accounts = this.filteredAccounts();
    return isSingleAccount || (isRequiredUpdatesPending && size(accounts) <= 1);
  };

  displayOverviewOption = () => {
    const userUpdatingPortfolio = [
      IndividualInvestmentProfileUpdate,
      IndividualPortfolioUpdate,
      // userState is string | undefined; includes() needs string (undefined
      // simply never matches at runtime).
    ].includes(this.props.userState as string);
    const isMultiAccount = size(this.props.accounts) >= 2;
    const isRequiredUpdatesPending =
      this.props.userState === IndividualRequiredUpdatesPending;
    const containsUserScopedRequiredUpdates =
      this.props.pendingRequiredUpdates.some(
        (update) => update.entityIdentifier.type === "User"
      );

    return (
      (!userUpdatingPortfolio && isMultiAccount && !isRequiredUpdatesPending) ||
      (isRequiredUpdatesPending && containsUserScopedRequiredUpdates)
    );
  };

  render() {
    return (
      <>
        <div className="account-switcher">
          <NavDropdown
            disabled={this.disableAccountSwitcher()}
            title={this._activeAccountName()}
            // @ts-expect-error preserve the original `null` (not undefined) for
            // the non-overview branch; react-bootstrap treats it as no handler.
            onClick={this.props.isAccountOverview ? this._handleRotate : null}
            id="nav-dropdown"
          >
            {this._getAccountTypes()}
          </NavDropdown>
        </div>
      </>
    );
  }
}

export default connector(withApollo<AccountSwitcherProps>(AccountSwitcher));
